import { inject, Injectable } from '@angular/core'
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms'

import { BehaviorSubject, firstValueFrom, startWith, takeUntil } from 'rxjs'

import {
  Action,
  AppFeatures,
  FULL_PERMISSIONS,
  Resource,
  Role,
  UserPermissions,
} from '@ti/contracts'
import { Memoize } from '@ti/core/common/decorators'
import { PricingPlanService } from '@ti/core/common/services'
import { exposeReadonlyState, formState, toSignalState } from '@ti/core/common/utils'
import { ViewModel } from '@ti/core/models/view.model'

import { RolesManager } from '../services'

export enum RoleFormMode {
  Add = 'add',
  Edit = 'edit',
  View = 'view',
}

/** A single permission, i.e. one action on one resource. */
export type PermissionRef = [Resource, Action]

/**
 * A permission dependency, resolved in both directions and transitively:
 * enabling the dependent enables everything it requires, and disabling anything
 * it requires disables the dependent.
 *
 * Dependencies are never auto-relaxed: unticking the dependent leaves the
 * permissions it required untouched.
 */
export interface PermissionDependency {
  /** The dependent resource. */
  resource: Resource
  /** Any of these actions on `resource` carries the dependency. */
  actions: Action[]
  /** Everything the dependent needs. */
  requires: PermissionRef[]
}

@Injectable()
export abstract class BaseRoleEditModel extends ViewModel {
  protected readonly rolesManager = inject(RolesManager)
  protected readonly pricingPlanService = inject(PricingPlanService)

  public abstract allowedActions: Action[]
  public abstract allowedResources: Resource[]
  public abstract defaultPermissions: UserPermissions
  public abstract lockedActions: Partial<Record<Resource, Action[]>>
  public abstract permissionDependencies: PermissionDependency[]

  /**
   * Resources that keep their form control and their saved permissions but are never
   * offered in the matrix. Hiding instead of dropping them from `allowedResources` is
   * deliberate: the payload must still carry them, so a role saved on any plan keeps
   * the permissions it already holds.
   */
  public readonly unofferedResources: Resource[] = []

  /**
   * Features the fleet's pricing plan must grant before a resource is offered in the
   * matrix. A resource absent from this map is offered on every plan. As above, a
   * resource the plan does not grant is only hidden - its control and its saved
   * permissions survive, because a downgrade must not strip a role's access.
   */
  public readonly resourcePlanFeatures: Partial<Record<Resource, AppFeatures[]>> = {}

  /**
   * Resolved with the plan lookup, and empty until then so nothing is frozen before the
   * matrix knows what it is showing.
   */
  protected hiddenResources = new Set<Resource>()

  protected atLeastOnePermissionValidator: ValidatorFn = (
    control: AbstractControl,
  ): ValidationErrors | null => {
    const permissions = control.value
    if (!permissions || typeof permissions !== 'object') {
      return { noPermissionsSelected: true }
    }

    const hasAtLeastOneAction = Object.values(permissions).some((actions) => {
      return Array.isArray(actions) && actions.length > 0
    })
    return hasAtLeastOneAction ? null : { noPermissionsSelected: true }
  }

  public readonly form = new FormGroup({
    name: new FormControl<string>('', [Validators.required]),
    description: new FormControl<string>(''),
    permissions: new FormGroup({}, { validators: this.atLeastOnePermissionValidator }),
  })

  // For templates of components that read the form but do not own it
  public readonly formState = formState(this.form, this.injector)

  protected readonly store$ = {
    visibleResources: new BehaviorSubject<Resource[]>([]),
    headerCheckboxIcons: new BehaviorSubject<Map<Action, string>>(new Map()),
    isFormValid: new BehaviorSubject<boolean>(false),
    roleFormMode: new BehaviorSubject<RoleFormMode | undefined>(undefined),
    editedRoleData: new BehaviorSubject<Role | undefined>(undefined),
    isSaving: new BehaviorSubject<boolean>(false),
  }

  @Memoize()
  public get state() {
    return toSignalState(this.state$, this.injector)
  }

  @Memoize()
  public get state$() {
    return exposeReadonlyState(this.store$, [
      'visibleResources',
      'headerCheckboxIcons',
      'isFormValid',
      'roleFormMode',
      'editedRoleData',
      'isSaving',
    ])
  }

  public add() {
    this.store$.roleFormMode.next(RoleFormMode.Add)
    // A new role has nothing to preserve, so the seed is trimmed to what the plan offers. Seeding a
    // hidden resource would grant access the author can neither see nor clear.
    const permissions = Object.fromEntries(
      Object.entries(this.defaultPermissions).filter(
        ([resource]) => !this.hiddenResources.has(resource as Resource),
      ),
    ) as UserPermissions
    this.setRole({ permissions })
  }

  public async edit(role: Role) {
    const isCustom = this.rolesManager.isCustom(role.id)
    const editAllowed = await this.accessControl.check(Resource.Role, Action.Update)
    if (isCustom && editAllowed) {
      this.store$.roleFormMode.next(RoleFormMode.Edit)
    } else {
      this.store$.roleFormMode.next(RoleFormMode.View)
    }
    this.store$.editedRoleData.next(role)
    this.setRole(role)
  }

  public async submitCreate() {
    const data = this.form.getRawValue() as Partial<Role>
    this.store$.isSaving.next(true)
    await this.rolesManager.create(data)
    this.resetForm()
  }

  public async submitUpdate() {
    const role = this.store$.editedRoleData.value
    const updates = this.form.getRawValue() as Partial<Role>
    if (role) {
      this.store$.isSaving.next(true)
      await this.rolesManager.update(role.id, updates)
      this.resetForm()
    }
  }

  public resetForm() {
    this.form.reset({
      name: '',
      description: '',
      permissions: {},
    })

    const permissionsGroup = this.form.get('permissions') as FormGroup
    this.allowedResources.forEach((res) => {
      permissionsGroup.get(res)?.setValue([], { emitEvent: false })
    })

    this.form.controls.permissions.enable()
    this.store$.roleFormMode.next(undefined)
    this.store$.editedRoleData.next(undefined)
    this.store$.isSaving.next(false)
    this.store$.isFormValid.next(false)
    this.store$.headerCheckboxIcons.next(new Map())
  }

  protected setRole(role: Partial<Role>) {
    this.form.patchValue(
      {
        name: role.name || '',
        description: role.description || '',
      },
      { emitEvent: false },
    )

    const permissionsGroup = this.form.get('permissions') as FormGroup
    const incomingPermissions = (role.permissions || {}) as Record<string, Action[]>

    this.allowedResources.forEach((res) => {
      const control = permissionsGroup.get(res)
      if (control) {
        const newActions = incomingPermissions[res] || []
        control.setValue(newActions, { emitEvent: false })
      }
    })

    this.form.updateValueAndValidity()
  }

  public toggleColumn(action: Action) {
    const currentMap = this.store$.headerCheckboxIcons.getValue()
    const currentIcon = currentMap.get(action)

    const shouldCheck = !currentIcon || currentIcon === ''

    this.store$.visibleResources.getValue().forEach((res) => {
      if (this.isActionAllowed(res, action)) {
        this.calculateResourceActions(res, action, shouldCheck)
      }
    })

    this.calculateHeaderIcons()
  }

  public isActionAllowed(resource: Resource, action: Action): boolean {
    const allowed = FULL_PERMISSIONS[resource]
    return allowed ? allowed.includes(action) : false
  }

  /** `undefined` for a resource without a form control. */
  public resourcePermissions(resource: Resource): Action[] | undefined {
    return (this.formState.value().permissions as Partial<Record<Resource, Action[]>>)[resource]
  }

  public isActionLocked(resource: Resource, action: Action): boolean {
    return this.lockedActions[resource]?.includes(action) ?? false
  }

  public hasPermission(resource: Resource, action: Action): boolean {
    return this.getResourceControl(resource)?.value?.includes(action) ?? false
  }

  protected override async init() {
    await this.initForm()
  }

  protected async initForm() {
    this.languageService.current$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.initAllowedResources())

    const permissionsGroup = this.form.get('permissions') as FormGroup
    this.allowedResources.forEach((res) => {
      if (!permissionsGroup.contains(res)) {
        permissionsGroup.addControl(res, new FormControl<Action[]>([]))
      }
    })

    this.form.valueChanges
      .pipe(takeUntil(this.destroy$), startWith(this.form.value))
      .subscribe(() => {
        this.calculateHeaderIcons()
        this.store$.isFormValid.next(this.form.valid)
      })
  }

  protected async initAllowedResources(): Promise<void> {
    const translationKeys = this.allowedResources.reduce(
      (acc, resource) => {
        acc[resource] = `roles.resources.${resource}`
        return acc
      },
      {} as Record<Resource, string>,
    )

    const resourceLabels: Record<string, string> = await firstValueFrom(
      this.languageService.massTranslate$(translationKeys),
    )

    const resourcesWithLabels = this.allowedResources.map((resource) => {
      const resourceKey = resource as keyof typeof resourceLabels
      return {
        resource: resource,
        label: resourceLabels[resourceKey],
      }
    })
    // Sort allowedResources alphabetically based on its labels
    resourcesWithLabels.sort((a, b) => a.label.localeCompare(b.label))
    this.allowedResources = resourcesWithLabels.map((item) => item.resource)

    const offeredResources = this.allowedResources.filter(
      (resource) => !this.unofferedResources.includes(resource),
    )
    const requiredFeatures = offeredResources.reduce(
      (acc, resource) => {
        acc[resource] = this.resourcePlanFeatures[resource] ?? []
        return acc
      },
      {} as Record<string, AppFeatures[]>,
    )
    const planAllows = await this.pricingPlanService.massAreFeaturesAllowed(requiredFeatures)
    const visibleResources = offeredResources.filter((resource) => planAllows[resource])

    this.hiddenResources = new Set(
      this.allowedResources.filter((resource) => !visibleResources.includes(resource)),
    )
    this.store$.visibleResources.next(visibleResources)
    this.calculateHeaderIcons()
  }

  protected calculateHeaderIcons(): void {
    const newMap = new Map<Action, string>()
    const permissionsRaw = (this.form.get('permissions')?.value || {}) as UserPermissions
    const visibleResources = this.store$.visibleResources.getValue()

    this.allowedActions.forEach((action) => {
      const eligibleResources = visibleResources.filter((res) => this.isActionAllowed(res, action))

      if (eligibleResources.length === 0) {
        newMap.set(action, '')
        return
      }

      let checkedCount = 0
      eligibleResources.forEach((res) => {
        const selectedActions: Action[] = permissionsRaw[res] || []
        if (selectedActions.includes(action)) {
          checkedCount++
        }
      })

      if (checkedCount === 0) {
        newMap.set(action, '')
      } else if (checkedCount === eligibleResources.length) {
        newMap.set(action, 'pi pi-check')
      } else {
        newMap.set(action, 'pi pi-minus')
      }
    })

    this.store$.headerCheckboxIcons.next(newMap)
  }

  public calculateResourceActions(resource: Resource, action: Action, value: boolean) {
    if (this.isActionLocked(resource, action) && !value) {
      return
    }

    this.setPermission(resource, action, value)
    this.propagate(resource, action, value, new Set())
  }

  protected setPermission(resource: Resource, action: Action, value: boolean) {
    const control = this.getResourceControl(resource)
    if (!control) return

    const currentActions = new Set(control.value || [])

    if (value) {
      currentActions.add(action)
      // Add View if any other action is added
      if (action !== Action.View) currentActions.add(Action.View)
    } else {
      // Remove all if View is removed
      if (action === Action.View) {
        currentActions.clear()
      } else {
        currentActions.delete(action)
      }
    }

    control.setValue(Array.from(currentActions))
  }

  @Memoize()
  protected get permissionIndex(): {
    dependencies: Map<string, PermissionRef[]>
    dependents: Map<string, PermissionRef[]>
  } {
    const dependencies = new Map<string, PermissionRef[]>()
    const dependents = new Map<string, PermissionRef[]>()

    const add = (index: Map<string, PermissionRef[]>, key: string, ref: PermissionRef) => {
      const refs = index.get(key)
      if (refs) {
        refs.push(ref)
      } else {
        index.set(key, [ref])
      }
    }

    this.permissionDependencies.forEach(({ resource, actions, requires }) => {
      actions.forEach((action) => {
        const dependentKey = this.permissionKey(resource, action)
        requires.forEach((required) => {
          add(dependencies, dependentKey, required)
          add(dependents, this.permissionKey(required[0], required[1]), [resource, action])
        })
      })
    })

    return { dependencies, dependents }
  }

  public dependenciesOf(resource: Resource, action: Action): PermissionRef[] {
    return this.permissionIndex.dependencies.get(this.permissionKey(resource, action)) ?? []
  }

  public dependentsOf(resource: Resource, action: Action): PermissionRef[] {
    return this.permissionIndex.dependents.get(this.permissionKey(resource, action)) ?? []
  }

  protected permissionKey(resource: Resource, action: Action): string {
    return `${resource}:${action}`
  }

  protected propagate(resource: Resource, action: Action, value: boolean, visited: Set<string>) {
    // Mutually dependent declarations only terminate because a permission is walked at most once
    const key = `${this.permissionKey(resource, action)}:${value}`
    if (visited.has(key)) return
    visited.add(key)

    const targets = value
      ? this.dependenciesOf(resource, action)
      : this.dependentsOf(resource, action)

    targets.forEach(([targetResource, targetAction]) => {
      // A hidden row is frozen. The user cannot see it, so no click of theirs may change it, and a
      // cascade must never strip permissions a role holds on a resource its plan no longer offers.
      if (this.hiddenResources.has(targetResource)) return
      if (!value && this.isActionLocked(targetResource, targetAction)) return

      // The walk continues even when the target already holds the requested state. A role saved
      // before a dependency existed can hold a dependent without the permission that links it to
      // the rest of the chain, and stopping here would strand everything behind that gap.
      if (this.hasPermission(targetResource, targetAction) !== value) {
        this.setPermission(targetResource, targetAction, value)
      }
      this.propagate(targetResource, targetAction, value, visited)
    })

    if (value && action !== Action.View) {
      // setPermission added View alongside this action, so View's own dependencies apply too
      this.propagate(resource, Action.View, true, visited)
    }

    if (!value && action === Action.View) {
      // setPermission cleared every action on the resource, so their dependents must follow
      const clearedActions = FULL_PERMISSIONS[resource] || []
      clearedActions.forEach((clearedAction) => {
        if (clearedAction !== Action.View) {
          this.propagate(resource, clearedAction, false, visited)
        }
      })
    }
  }

  protected getResourceControl(resource: Resource): FormControl<Action[]> | null {
    return this.form.get(['permissions', resource]) as FormControl<Action[]> | null
  }
}
